/**
 * Aerial night neighborhood — half dark (grid failure), half lit (BIN powered).
 * Utility poles carry BIN Community Nodes, connected via underground line hints.
 */
export default function NeighborhoodHero() {
  // House template
  const House = ({ x, y, lit }) => (
    <g transform={`translate(${x}, ${y})`}>
      {/* body */}
      <rect x="-24" y="-16" width="48" height="34" fill={lit ? "#1a2842" : "#0f1a2d"} stroke="#243553" strokeWidth="1" />
      {/* roof */}
      <polygon points="-28,-16 0,-34 28,-16" fill={lit ? "#243553" : "#152238"} stroke="#2b3f63" strokeWidth="1" />
      {/* windows */}
      <rect x="-16" y="-8" width="10" height="10" fill={lit ? "#F5A623" : "#0a1120"} className={lit ? "bin-home-lit" : ""} />
      <rect x="6" y="-8" width="10" height="10" fill={lit ? "#F5A623" : "#0a1120"} className={lit ? "bin-home-lit" : ""} />
      {/* door */}
      <rect x="-5" y="2" width="10" height="16" fill={lit ? "#F5A623" : "#0a1120"} opacity={lit ? 0.7 : 1} />
      {/* halo if lit */}
      {lit && (
        <ellipse cx="0" cy="0" rx="60" ry="30" fill="#F5A623" opacity="0.06" />
      )}
    </g>
  );

  // Utility pole with BIN Community Node
  const Pole = ({ x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
      {/* pole */}
      <line x1="0" y1="0" x2="0" y2="70" stroke="#3a4e70" strokeWidth="2" />
      {/* crossbar */}
      <line x1="-12" y1="12" x2="12" y2="12" stroke="#3a4e70" strokeWidth="1.5" />
      {/* BIN Node box */}
      <rect x="-9" y="-8" width="18" height="12" fill="#0A1628" stroke="#F5A623" strokeWidth="1.5" />
      <circle cx="0" cy="-2" r="3" fill="#F5A623">
        <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
      </circle>
      {/* solar panel on top */}
      <polygon points="-14,-14 14,-14 12,-8 -12,-8" fill="#152238" stroke="#F5A623" strokeWidth="0.8" opacity="0.85" />
      {/* pulsating aura */}
      <circle cx="0" cy="0" r="18" fill="none" stroke="#F5A623" strokeWidth="0.8" opacity="0.5">
        <animate attributeName="r" from="8" to="28" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="opacity" from="0.7" to="0" dur="2.4s" repeatCount="indefinite" />
      </circle>
    </g>
  );

  return (
    <svg viewBox="0 0 1400 720" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
      <defs>
        <linearGradient id="nh-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#050B18" />
          <stop offset="60%" stopColor="#0A1628" />
          <stop offset="100%" stopColor="#101f38" />
        </linearGradient>
        <radialGradient id="nh-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F5A623" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#F5A623" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="nh-ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a1425" />
          <stop offset="100%" stopColor="#050a14" />
        </linearGradient>
      </defs>

      {/* Sky */}
      <rect width="1400" height="720" fill="url(#nh-sky)" />

      {/* subtle stars */}
      <g>
        {Array.from({ length: 60 }).map((_, i) => (
          <circle
            key={i}
            cx={(i * 91) % 1400}
            cy={(i * 47) % 260}
            r={i % 5 === 0 ? 1.4 : 0.7}
            fill={i % 7 === 0 ? "#F5A623" : "#8ab0d8"}
            opacity={0.4 + (i % 3) * 0.2}
          />
        ))}
      </g>

      {/* Ground */}
      <rect x="0" y="440" width="1400" height="280" fill="url(#nh-ground)" />

      {/* Center road (horizon) */}
      <path d="M -50 620 L 700 490 L 1450 620 L 1450 720 L -50 720 Z" fill="#040812" />
      <line x1="700" y1="490" x2="0" y2="620" stroke="#F5A623" strokeWidth="1" opacity="0.15" strokeDasharray="4 12" />
      <line x1="700" y1="490" x2="1400" y2="620" stroke="#F5A623" strokeWidth="1" opacity="0.15" strokeDasharray="4 12" />

      {/* LEFT half — grid failure (dark) */}
      <g>
        {/* Underground line hint (dashed, dim) - none on this side */}
        <House x={140} y={470} lit={false} />
        <House x={260} y={490} lit={false} />
        <House x={380} y={510} lit={false} />
        <House x={520} y={530} lit={false} />
        <House x={180} y={565} lit={false} />
        <House x={310} y={585} lit={false} />
        <House x={450} y={605} lit={false} />
        <House x={80} y={640} lit={false} />
        <House x={230} y={655} lit={false} />
        <House x={380} y={670} lit={false} />
      </g>

      {/* RIGHT half — BIN powered (lit) */}
      <g>
        {/* Underground energy loop */}
        <path
          d="M 800 620 Q 950 600 1050 605 T 1300 615"
          fill="none"
          stroke="#F5A623"
          strokeWidth="1.5"
          opacity="0.55"
        />
        <path
          d="M 800 620 Q 950 600 1050 605 T 1300 615"
          fill="none"
          stroke="#F5A623"
          strokeWidth="1.8"
          strokeDasharray="6 8"
          style={{ animation: "bin-energy-flow 2.5s linear infinite" }}
        />
        <path
          d="M 850 555 Q 950 545 1080 550 T 1350 560"
          fill="none"
          stroke="#F5A623"
          strokeWidth="1"
          strokeDasharray="4 10"
          opacity="0.6"
          style={{ animation: "bin-energy-flow 3s linear infinite" }}
        />

        <House x={840} y={470} lit={true} />
        <House x={960} y={490} lit={true} />
        <House x={1080} y={510} lit={true} />
        <House x={1220} y={530} lit={true} />
        <House x={880} y={565} lit={true} />
        <House x={1010} y={585} lit={true} />
        <House x={1150} y={605} lit={true} />
        <House x={780} y={640} lit={true} />
        <House x={930} y={655} lit={true} />
        <House x={1080} y={670} lit={true} />
      </g>

      {/* Utility poles with BIN Community Nodes — on the BIN side */}
      <Pole x={720} y={520} />
      <Pole x={1000} y={505} />
      <Pole x={1280} y={520} />

      {/* Divider glow between failed and powered halves */}
      <line x1="700" y1="430" x2="700" y2="720" stroke="#F5A623" strokeWidth="1" opacity="0.15" strokeDasharray="2 6" />

      {/* Labels */}
      <g fontFamily="IBM Plex Mono, monospace" fontSize="10" letterSpacing="0.25em">
        <text x="70" y="435" fill="#ef4444" opacity="0.85">◉ GRID FAILURE</text>
        <text x="1210" y="435" fill="#F5A623" opacity="0.9">◉ BIN POWERED</text>
      </g>
    </svg>
  );
}
